import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import { GET_OPERATING_EXPENSES, GET_ALL_REFERRED_BY_OPTIONS } from "../../../../utils/GraphQL/queries";
import { SAVE_OPERATING_EXPENSE, DELETE_OPERATING_EXPENSE } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import { gqlErrorMessage } from "../../../../utils/gqlErrorCode";
import { CustomCalendar } from "../EventDateTimeInput/EventDateTimeInput";
import { priceStrFormatter, removePriceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { money, fmtDay } from "./doorMarginFormat";

/**
 * Operating costs that ride into the per-door overhead (plan §2.8 / D14 / D24
 * — web-only entry). Software is pulled automatically (Business Metrics
 * panel) and never entered here. Recurring per-source marketing costs live on
 * Defaults → Referral Sources (with an effective-from date so months can be
 * backfilled) and are DISPLAYED here read-only; one-off marketing spends,
 * damage/warranty costs and anything else are entered as periods so a
 * quarterly bill prorates by day into whichever snapshot windows it touches.
 */
const CATEGORIES = [
  ["marketing", "Marketing (one-off)"],
  ["damage", "Damage / warranty"],
  ["other", "Other"],
];

const blank = () => ({ id: null, category: "damage", sourceOptionId: "", amount: "", periodStart: null, periodEnd: null, note: "" });

const OperatingExpensesPanel = ({ isAdmin }) => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  const [form, setForm] = useState(blank());
  const [cal, setCal] = useState(null); // "start" | "end"

  const { data, refetch } = useQuery(GET_OPERATING_EXPENSES, { fetchPolicy: "network-only" });
  const { data: srcData } = useQuery(GET_ALL_REFERRED_BY_OPTIONS, { fetchPolicy: "cache-and-network" });
  const [save, { loading: saving }] = useMutation(SAVE_OPERATING_EXPENSE);
  const [del] = useMutation(DELETE_OPERATING_EXPENSE);

  const expenses = data?.getOperatingExpenses || [];
  const sources = useMemo(() => [...(srcData?.getAllReferredByOptions || [])].sort((a, b) => (a.source || "").localeCompare(b.source || "")), [srcData]);
  const paidSources = sources.filter((s) => (s.costHistory || []).length > 0);

  const currentMonthly = (s) => {
    const now = Date.now();
    let best = null;
    for (const e of s.costHistory || []) {
      if (Number(e.effectiveFrom) <= now && (!best || Number(e.effectiveFrom) >= Number(best.effectiveFrom))) best = e;
    }
    return best;
  };

  const startEdit = (e) => {
    setForm({
      id: e.id,
      category: e.category,
      sourceOptionId: e.sourceOptionId || "",
      amount: priceStrFormatter(e.amountCents),
      periodStart: Number(e.periodStart),
      periodEnd: Number(e.periodEnd),
      note: e.note || "",
    });
  };

  const onSave = async () => {
    const cents = removePriceStrFormatter(form.amount || "");
    if (!form.periodStart || !form.periodEnd) {
      handleSnack("Pick the period this cost covers", 3000);
      return;
    }
    if (Number(form.periodEnd) < Number(form.periodStart)) {
      handleSnack("The period end must be on or after its start", 3000);
      return;
    }
    try {
      await save({
        variables: {
          input: {
            id: form.id,
            category: form.category,
            sourceOptionId: form.category === "marketing" && form.sourceOptionId ? form.sourceOptionId : null,
            amountCents: cents,
            periodStart: String(form.periodStart),
            periodEnd: String(form.periodEnd),
            note: form.note.trim() || null,
          },
        },
      });
      setForm(blank());
      await refetch();
      handleSnack("Operating cost saved — recompute to see it per door", 3500);
    } catch (err) {
      handleSnack(`Save failed: ${gqlErrorMessage(err)}`, 5000);
    }
  };

  const onDelete = async (e) => {
    const ok = await confirm({ title: "Remove this cost?", message: `${money(e.amountCents)} · ${e.category}`, confirmLabel: "Remove" });
    if (!ok) return;
    try {
      await del({ variables: { id: e.id } });
      await refetch();
      handleSnack("Removed", 2500);
    } catch (err) {
      handleSnack(`Remove failed: ${gqlErrorMessage(err)}`, 5000);
    }
  };

  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <h3 className="bm-panel-title">Operating costs</h3>
      <p className="bm-panel-note">
        Software is pulled automatically (see Business → Software Expenses). Recurring marketing per source is set on
        Defaults → Referral Sources with an effective-from date; one-off spends, damage and other costs go here as a period.
      </p>

      <h4 className="dm-h4">Recurring marketing by source</h4>
      {paidSources.length === 0 ? (
        <div className="bm-empty-small">No source has a monthly cost yet — set one under Defaults → Referral Sources.</div>
      ) : (
        <div className="bm-table-scroll">
          <table className="bm-table">
            <thead>
              <tr>
                <th>Source</th>
                <th className="bm-num">Monthly (now)</th>
                <th>Since</th>
                <th className="bm-num">History</th>
              </tr>
            </thead>
            <tbody>
              {paidSources.map((s) => {
                const cur = currentMonthly(s);
                return (
                  <tr key={s._id}>
                    <td>{s.source}</td>
                    <td className="bm-num bm-money">{cur ? money(cur.monthlyCents) : "--"}</td>
                    <td>{cur ? fmtDay(cur.effectiveFrom) : "--"}</td>
                    <td className="bm-num bm-muted">{s.costHistory.length} change{s.costHistory.length === 1 ? "" : "s"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h4 className="dm-h4">Entered costs</h4>
      {expenses.length === 0 ? (
        <div className="bm-empty-small">Nothing entered yet.</div>
      ) : (
        <div className="bm-table-scroll">
          <table className="bm-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Period</th>
                <th className="bm-num">Amount</th>
                <th>Note</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id}>
                  <td>
                    {CATEGORIES.find(([k]) => k === e.category)?.[1] || e.category}
                    {e.sourceName ? <span className="bm-muted"> · {e.sourceName}</span> : null}
                  </td>
                  <td>
                    {fmtDay(e.periodStart)} – {fmtDay(e.periodEnd)}
                  </td>
                  <td className="bm-num bm-money">{money(e.amountCents)}</td>
                  <td className="bm-muted">{e.note || ""}</td>
                  {isAdmin && (
                    <td className="bm-num">
                      <button type="button" className="dm-link" onClick={() => startEdit(e)}>
                        edit
                      </button>{" "}
                      <button type="button" className="dm-link dm-link-danger" onClick={() => onDelete(e)}>
                        remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <div className="dm-expense-form">
          <h4 className="dm-h4">{form.id ? "Edit cost" : "Add a cost"}</h4>
          <div className="dm-form-grid">
            <label className="dm-field">
              <span>Category</span>
              <BdrSelectPopup
                id="dm-oe-category"
                className="dm-select bdr-select-trigger"
                label="Category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                options={CATEGORIES.map(([k, label]) => ({ value: k, label }))}
              />
            </label>
            {form.category === "marketing" && (
              <label className="dm-field">
                <span>Source (optional)</span>
                <BdrSelectPopup
                  id="dm-oe-source"
                  className="dm-select bdr-select-trigger"
                  label="Source (optional)"
                  value={form.sourceOptionId}
                  onChange={(e) => setForm((f) => ({ ...f, sourceOptionId: e.target.value }))}
                  placeholder="— blended —"
                  options={sources.map((s) => ({
                    value: s._id,
                    label: s.source,
                  }))}
                />
              </label>
            )}
            <label className="dm-field">
              <span>Amount</span>
              <input
                className="dm-input dm-input-money"
                value={form.amount}
                inputMode="decimal"
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value === "" ? "" : priceStrFormatter(removePriceStrFormatter(e.target.value)) }))}
              />
            </label>
            <label className="dm-field">
              <span>Period start</span>
              <button type="button" className="dm-input dm-input-btn" onClick={() => setCal("start")}>
                {form.periodStart ? fmtDay(form.periodStart) : "Pick"}
              </button>
            </label>
            <label className="dm-field">
              <span>Period end</span>
              <button type="button" className="dm-input dm-input-btn" onClick={() => setCal("end")}>
                {form.periodEnd ? fmtDay(form.periodEnd) : "Pick"}
              </button>
            </label>
            <label className="dm-field dm-field-wide">
              <span>Note</span>
              <input className="dm-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            </label>
          </div>
          <div className="dm-form-row">
            {form.id && (
              <button type="button" className="shop-inv-modal-cancel-btn" onClick={() => setForm(blank())}>
                Cancel
              </button>
            )}
            <button type="button" className="shop-inv-modal-save-btn bdr-grad-cal-green" disabled={saving} onClick={onSave}>
              {saving ? "Saving…" : form.id ? "Save changes" : "Add cost"}
            </button>
          </div>
        </div>
      )}

      {cal && (
        <CustomCalendar
          initialDate={form[cal === "start" ? "periodStart" : "periodEnd"] ? new Date(form[cal === "start" ? "periodStart" : "periodEnd"]) : null}
          onSelect={(d) => {
            const day = new Date(d);
            day.setHours(12, 0, 0, 0); // noon — safely inside the picked day
            setForm((f) => ({ ...f, [cal === "start" ? "periodStart" : "periodEnd"]: day.getTime() }));
            setCal(null);
          }}
          onCancel={() => setCal(null)}
        />
      )}
    </div>
  );
};

export default OperatingExpensesPanel;
